import { ChatSidebar } from '@roko/ui/components/chat/ChatSidebar'
import { Button } from '@roko/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@roko/ui/components/ui/dropdown-menu'
import { Sheet, SheetContent } from '@roko/ui/components/ui/sheet'
import { Switch } from '@roko/ui/components/ui/switch'
import { FALSE, TRUE } from '@roko/ui/constants/common'
import { IconChevronDown, IconMenu2 } from '@tabler/icons-react'
import { Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { hasAdminGroupAccess } from '@/auth/adminAccess'
import { FeatureFlagDevPanel } from '@/components/layout/FeatureFlagDevPanel'
import { TopNavigation } from '@/components/layout/TopNavigation'
import { ADMIN_NAV_CONFIG } from '@/constants/adminNav'
import { baseFeatures } from '@/constants/features'
import { THEME } from '@/constants/layout'
import { ChatProvider, useChatContext } from '@/context/ChatProvider'
import { FeatureFlagProvider } from '@/context/FeatureProvider'
import { useAuthStore } from '@/store/authStore'
import { useLayoutStore } from '@/store/useLayoutStore'
import { isDev, isLocal } from '@/utils/env'

const LayoutContent: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const userName = useAuthStore((state) => state.user?.userName)
  const userEmail = useAuthStore((state) => state.user?.email)
  const logout = useAuthStore((state) => state.logout)
  const themeState = useLayoutStore((state) => state.themeState)
  const setTheme = useLayoutStore((state) => state.setTheme)
  const isDesktopSidebarCollapsed = useLayoutStore((state) => state.isDesktopSidebarCollapsed)
  const toggleDesktopSidebarCollapsed = useLayoutStore((state) => state.toggleDesktopSidebarCollapsed)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(FALSE)
  const { chats, activeChatId, isLoadingChats, selectChat, createChat, deleteChat } = useChatContext()

  const isAdminPage = pathname === '/documents' || pathname === '/web-pages' || pathname === '/reports'
  const activeChatTitle = useMemo(() => chats.find((chat) => chat.id === activeChatId)?.title, [activeChatId, chats])

  const displayName = userName || t('chat.anonymousUser')
  const displayEmail = userEmail || t('chat.anonymousEmail')
  const isDarkTheme =
    themeState === THEME.DARK || (themeState === THEME.SYSTEM && window.matchMedia('(prefers-color-scheme: dark)').matches)

  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const canManageSources = isAuthenticated && hasAdminGroupAccess()

  const adminNavItems = useMemo(
    () =>
      canManageSources
        ? ADMIN_NAV_CONFIG.map((item) => ({
            ...item,
            label: t(item.labelKey),
          }))
        : [],
    [canManageSources, t]
  )

  const handleSelectChat = (id: string) => {
    selectChat(id)
    setIsMobileSidebarOpen(FALSE)
    void navigate({ to: '/' })
  }

  const handleNewChat = () => {
    createChat()
    setIsMobileSidebarOpen(FALSE)
    void navigate({ to: '/' })
  }

  const profileInitial = displayName.slice(0, 1).toUpperCase()

  const themeMenuItems = (
    <>
      <DropdownMenuItem className="flex cursor-default items-center justify-between gap-4" onSelect={(event) => event.preventDefault()}>
        <span>{t('navigation.darkMode')}</span>
        <Switch checked={isDarkTheme} onCheckedChange={(checked) => setTheme(checked ? THEME.DARK : THEME.LIGHT)} />
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuItem onClick={() => void logout()}>{t('navigation.logout')}</DropdownMenuItem>
    </>
  )

  const userMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="flex w-full items-center gap-2.5 rounded-lg px-1 py-1.5 text-left hover:bg-muted/80">
          <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
            {profileInitial}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">{displayName}</p>
            <p className="truncate text-xs text-muted-foreground">{displayEmail}</p>
          </div>
          <IconChevronDown className="size-4 shrink-0 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-44">
        {themeMenuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const collapsedUserMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          title={displayName}
          aria-label={displayName}
          className="flex size-10 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground transition-colors hover:bg-muted/80"
        >
          {profileInitial}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="right" className="min-w-44">
        {themeMenuItems}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const renderSidebar = (isCollapsed: boolean, options?: { allowCollapse?: boolean }) => (
    <ChatSidebar
      createNewChatLabel={t('chat.createNewChat')}
      searchPlaceholder={t('chat.searchPlaceholder')}
      deleteChatLabel={t('chat.deleteChat')}
      collapseSidebarLabel={t('navigation.collapseSidebar')}
      expandSidebarLabel={t('navigation.expandSidebar')}
      isCollapsed={isCollapsed}
      onToggleCollapsed={options?.allowCollapse === FALSE ? undefined : toggleDesktopSidebarCollapsed}
      chats={chats}
      isLoadingChats={isLoadingChats}
      loadingChatsLabel={t('chat.loadingChats')}
      showHistory
      adminNavItems={adminNavItems}
      onNewChat={handleNewChat}
      onDeleteChat={deleteChat}
      activeChatId={activeChatId}
      onSelectChat={handleSelectChat}
      userName={displayName}
      userEmail={displayEmail}
      userMenu={isCollapsed ? collapsedUserMenu : userMenu}
    />
  )

  return (
    <div className="flex h-screen overflow-hidden bg-background p-0 md:p-3">
      <div className="hidden md:flex">{renderSidebar(isDesktopSidebarCollapsed)}</div>

      <Sheet open={isMobileSidebarOpen} onOpenChange={setIsMobileSidebarOpen}>
        <SheetContent side="left" className="w-[17.5rem] border-0 bg-background p-0">
          {renderSidebar(FALSE, { allowCollapse: FALSE })}
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-card md:rounded-[1.5rem] md:shadow-sm">
        {!isAdminPage && (
          <TopNavigation
            title={activeChatTitle}
            onNewChat={handleNewChat}
            onOpenHistory={() => setIsMobileSidebarOpen(TRUE)}
            sidebarTrigger={
              <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setIsMobileSidebarOpen(TRUE)}>
                <IconMenu2 className="size-5" />
              </Button>
            }
          />
        )}
        {isAdminPage && (
          <div className="flex h-14 items-center px-4 md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileSidebarOpen(TRUE)}>
              <IconMenu2 className="size-5" />
            </Button>
          </div>
        )}
        <main className="min-h-0 flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

export const Layout: FC = () => {
  return (
    <FeatureFlagProvider initialFlags={baseFeatures} useRemote>
      <ChatProvider>
        <LayoutContent />
      </ChatProvider>
      {(isDev() || isLocal()) && <FeatureFlagDevPanel />}
    </FeatureFlagProvider>
  )
}
